import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect
} from "react";
import { Send, Maximize2 } from "lucide-react"; // CHANGED: Maximize2 instead of ArrowLeftRight
import decodeToken from "../../lib/decodeToken";
import { getTicketChats, sendTicketChat } from "../../lib/needHelpApi";
import NeedHelpTicketChat from "./NeedHelpTicketChat";
import styles from "./NeedHelpTickets.module.css";

// A small chat box for the ticket details screen: a fixed height box with the
// latest messages, a message input + send button and an expand icon that opens
// the existing chat popup.

const getToken = () => {
  try {
    return localStorage.getItem("token") || "";
  } catch (_e) {
    return "";
  }
};

const formatTime = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const MAX_LEN = 2000;
// How many of the latest messages the box shows.
const VISIBLE_COUNT = 3;

// Each message: { sentBy: "user" | "admin", message, isRead, timestamp }
// `onChatRead(ticketId)` tells the parent that the admin's messages were read
// (loading the chat makes the backend mark them as read).
const NeedHelpTicketChatPreview = ({ ticket, onChatRead }) => {
  const userId = useMemo(() => decodeToken(getToken())?.id || "", []);
  const listRef = useRef(null);
  // Used to keep the keyboard open after tapping send.
  const inputRef = useRef(null);

  // Kept in a ref so a new function from the parent never reloads the chat.
  const onChatReadRef = useRef(onChatRead);
  onChatReadRef.current = onChatRead;

  const [chats, setChats] = useState([]);
  // "loading" | "ready" | "error"
  const [status, setStatus] = useState("loading");
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [fullOpen, setFullOpen] = useState(false);

  const loadChats = useCallback(
    async ({ silent = false } = {}) => {
      // A silent reload keeps the messages on screen: no spinner.
      if (!silent) setStatus("loading");
      try {
        const list = await getTicketChats({
          ticketId: ticket.ticketId,
          userId,
          token: getToken(),
        });
        setChats(list);
        setStatus("ready");
        onChatReadRef.current?.(ticket.ticketId);
      } catch (_e) {
        if (!silent) setStatus("error");
      }
    },
    [ticket.ticketId, userId],
  );

  useEffect(() => {
    loadChats();
  }, [loadChats]);

  // Keep the newest message in view.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chats.length, status]);

  // Message box: 1 line to start, grows with the text up to 3 lines, then scrolls.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || 22;
    const padding =
      (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const border =
      (parseFloat(cs.borderTopWidth) || 0) +
      (parseFloat(cs.borderBottomWidth) || 0);
    const maxHeight = lineHeight * 3 + padding + border;
    const needed = el.scrollHeight + border;
    el.style.height = `${Math.min(needed, maxHeight)}px`;
    el.style.overflowY = needed > maxHeight ? "auto" : "hidden";
  }, [draft, status]);

  // When the full chat popup closes, reload quietly so messages sent there
  // show up here too.
  const closeFull = useCallback(() => {
    setFullOpen(false);
    loadChats({ silent: true });
  }, [loadChats]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending || status !== "ready") return;

    // Focus the input right away, inside the tap/click. On iOS Safari this
    // must happen before any await, otherwise the keyboard closes and cannot
    // be reopened by code.
    inputRef.current?.focus();

    // The message shows straight away (marked "Sending...") and is swapped
    // for the saved one when the backend answers.
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      sentBy: "user",
      message: text,
      isRead: false,
      timestamp: new Date().toISOString(),
      tempId,
      pending: true,
    };

    setSending(true);
    setSendError("");
    setChats((prev) => [...prev, optimistic]);
    setDraft("");

    try {
      const chat = await sendTicketChat({
        ticketId: ticket.ticketId,
        userId,
        message: text,
        token: getToken(),
      });
      // tempId is kept so the message keeps the same React key (no flicker).
      setChats((prev) =>
        prev.map((c) => (c.tempId === tempId ? { ...chat, tempId } : c)),
      );
    } catch (_e) {
      // Not saved: take the message back out and give the text back.
      setChats((prev) => prev.filter((c) => c.tempId !== tempId));
      setDraft((prev) => prev || text);
      setSendError("Your message wasn't sent. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    // Desktop (mouse + keyboard): Enter sends, Shift+Enter = new line.
    // Touch devices: Enter = new line, use the send button.
    if (e.key === "Enter" && !e.shiftKey) {
      const desktop =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(pointer: fine)").matches;
      if (desktop) {
        e.preventDefault();
        send();
      }
    }
  };

  const firstVisible = Math.max(0, chats.length - VISIBLE_COUNT);
  const visible = chats.slice(firstVisible);
  // Older messages exist: show the white fade at the top.
  const hasOlder = firstVisible > 0;

  return (
    <section className={styles.chatPrev}>
      <div className={styles.chatPrevBox}>
        {/* CHANGED: expand icon (white on a dark round background), placed in
            the top right corner of the box. The old .chatPrevTop wrapper is
            gone because the button is positioned absolutely now. */}
        {status === "ready" && chats.length > 0 && (
          <button
            type="button"
            className={styles.chatPrevFull}
            onClick={() => setFullOpen(true)}
            aria-label="Open full chat"
            title="Open full chat"
          >
            <Maximize2 size={14} strokeWidth={2.2} aria-hidden="true" />
          </button>
        )}

        <div className={styles.chatPrevListWrap}>
          <div ref={listRef} className={styles.chatPrevList}>
            {status === "loading" && (
              <div className={styles.chatCenter}>
                <span className={styles.spinner} aria-label="Loading chat" />
              </div>
            )}

            {status === "error" && (
              <div className={styles.chatCenter}>
                <p className={styles.emptyText}>
                  We couldn't load this chat. Check your connection and try
                  again.
                </p>
                <button
                  type="button"
                  className={styles.centerBtn}
                  onClick={() => loadChats()}
                >
                  Try again
                </button>
              </div>
            )}

            {status === "ready" && chats.length === 0 && (
              <div className={styles.chatCenter}>
                <p className={styles.emptyText}>
                  No messages yet. Send a message to start the conversation.
                </p>
              </div>
            )}

            {status === "ready" &&
              visible.map((m, i) => {
                const mine = m.sentBy === "user";
                return (
                  <div
                    key={m.tempId || `${m.timestamp}-${firstVisible + i}`}
                    className={`${styles.chatRow} ${
                      mine ? styles.chatRowMine : styles.chatRowTheirs
                    }`}
                  >
                    <div
                      className={`${styles.chatBubble} ${
                        mine ? styles.chatBubbleMine : styles.chatBubbleTheirs
                      }`}
                    >
                      {m.message}
                    </div>
                    <span className={styles.chatMeta}>
                      {!mine && "Support · "}
                      {m.pending
                        ? "Sending..."
                        : `${formatTime(m.timestamp)}${
                            mine ? ` · ${m.isRead ? "Seen" : "Sent"}` : ""
                          }`}
                    </span>
                  </div>
                );
              })}
          </div>

          {hasOlder && status === "ready" && (
            <div className={styles.chatPrevFade} aria-hidden="true" />
          )}
        </div>

        {sendError && <p className={styles.chatError}>{sendError}</p>}

        <div className={`${styles.chatComposer} ${styles.chatPrevComposer}`}>
          <textarea
            ref={inputRef}
            className={styles.chatInput}
            placeholder="Type your message..."
            value={draft}
            maxLength={MAX_LEN}
            rows={1}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={status !== "ready"}
          />
          <button
            type="button"
            className={styles.chatSendBtn}
            // Stops the button from stealing focus from the textarea, so the
            // mobile keyboard stays open when send is tapped.
            onMouseDown={(e) => e.preventDefault()}
            onClick={send}
            disabled={status !== "ready" || sending || !draft.trim()}
            aria-label="Send message"
          >
            <Send size={17} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      </div>

      {fullOpen && (
        <NeedHelpTicketChat
          ticket={ticket}
          onClose={closeFull}
          onRead={() => onChatRead?.(ticket.ticketId)}
        />
      )}
    </section>
  );
};

export default NeedHelpTicketChatPreview;
